import React, { useState, useCallback, useMemo } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { AuditLogEntry, AuditListParams, AuditStatus } from '../../types/audit';
import UnifiedAuditDiff from './UnifiedAuditDiff';

const STATUS_ICON: Record<AuditStatus, string> = {
  success: '✓',
  failed: '✕',
  denied: '⛔',
};

interface AuditLogTableProps {
  items: AuditLogEntry[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  filters: AuditListParams;
  onFilterChange: (filters: AuditListParams) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onExportCsv?: () => void;
}

/**
 * Format a Date as a value for a `<input type="datetime-local">`.
 * Preserves local timezone (which is what the user expects).
 */
const toLocalInputValue = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

const toIsoFromInput = (local: string): string | undefined => {
  if (!local) return undefined;
  // datetime-local returns "YYYY-MM-DDTHH:MM" in local time.
  // Convert to an ISO-8601 string the backend can fromisoformat().
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

const fromIsoToInput = (iso: string | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return toLocalInputValue(d);
};

const AuditLogTable: React.FC<AuditLogTableProps> = ({
  items,
  loading,
  total,
  page,
  pageSize,
  filters,
  onFilterChange,
  onPageChange,
  onPageSizeChange,
  onExportCsv,
}) => {
  const { t, formatDate, formatRelativeTime } = useI18n();
  const [expanded, setExpanded] = useState<number | null>(null);
  // Auto-expand the advanced filters block if any of its fields is set
  // (so a deep-linked URL with eg. resource_id always shows what's active).
  const initialAdvancedOpen =
    !!filters.resource_id || !!filters.action || !!filters.actor_username
    || !!filters.from_timestamp || !!filters.to_timestamp;
  const [showAdvanced, setShowAdvanced] = useState<boolean>(initialAdvancedOpen);
  // Count of currently-set advanced filters — surfaces as a badge on the toggle.
  const advancedActiveCount = [
    filters.resource_id, filters.action, filters.actor_username,
    filters.from_timestamp, filters.to_timestamp,
  ].filter(v => !!v).length;

  const handleFilter = useCallback(
    (key: keyof AuditListParams, value: string | boolean | undefined) => {
      const next: AuditListParams = { ...filters };
      if (value === '' || value === undefined || value === false) {
        delete next[key];
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (next as any)[key] = value;
      }
      onFilterChange(next);
    },
    [filters, onFilterChange],
  );

  const setQuickRange = useCallback(
    (hours: number | null) => {
      const next: AuditListParams = { ...filters };
      if (hours === null) {
        delete next.from_timestamp;
        delete next.to_timestamp;
      } else {
        const to = new Date();
        const from = new Date(to.getTime() - hours * 3600 * 1000);
        next.from_timestamp = from.toISOString();
        next.to_timestamp = to.toISOString();
      }
      onFilterChange(next);
    },
    [filters, onFilterChange],
  );

  const stats = useMemo(() => {
    let success = 0;
    let failed = 0;
    let denied = 0;
    for (const it of items) {
      if (it.status === 'success') success++;
      else if (it.status === 'failed') failed++;
      else if (it.status === 'denied') denied++;
    }
    return { success, failed, denied };
  }, [items]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="audit-log-container">
      <div className="audit-quick-chips">
        <button type="button" className="audit-chip" onClick={() => setQuickRange(1)}>
          {t('audit.filters.lastHour')}
        </button>
        <button type="button" className="audit-chip" onClick={() => setQuickRange(24)}>
          {t('audit.filters.last24h')}
        </button>
        <button type="button" className="audit-chip" onClick={() => setQuickRange(24 * 7)}>
          {t('audit.filters.last7d')}
        </button>
        <button type="button" className="audit-chip" onClick={() => setQuickRange(24 * 30)}>
          {t('audit.filters.last30d')}
        </button>
        <button type="button" className="audit-chip" onClick={() => setQuickRange(null)}>
          {t('audit.filters.allTime')}
        </button>
        {onExportCsv && (
          <button
            type="button"
            className="audit-export-btn"
            onClick={onExportCsv}
            disabled={loading}
            data-testid="audit-export-csv"
          >
            ⬇ {t('audit.filters.exportCsv')}
          </button>
        )}
      </div>

      <div className="audit-stats-strip">
        <div className="audit-stat-card total" data-testid="audit-stat-total">
          <div className="audit-stat-label">{t('audit.stats.total')}</div>
          <div className="audit-stat-value">{total}</div>
        </div>
        <div className="audit-stat-card success" data-testid="audit-stat-success">
          <div className="audit-stat-label">{t('audit.stats.success')}</div>
          <div className="audit-stat-value">{stats.success}</div>
        </div>
        <div className="audit-stat-card failed" data-testid="audit-stat-failed">
          <div className="audit-stat-label">{t('audit.stats.failed')}</div>
          <div className="audit-stat-value">{stats.failed}</div>
        </div>
        <div className="audit-stat-card denied" data-testid="audit-stat-denied">
          <div className="audit-stat-label">{t('audit.stats.denied')}</div>
          <div className="audit-stat-value">{stats.denied}</div>
        </div>
      </div>

      <div className="audit-filters">
        <div className="filter-field">
          <label htmlFor="audit-filter-resource-type">
            {t('audit.filters.resourceType')}
          </label>
          <input
            id="audit-filter-resource-type"
            type="text"
            list="audit-resource-types"
            className="filter-input"
            value={filters.resource_type || ''}
            onChange={(e) => handleFilter('resource_type', e.target.value)}
            data-testid="audit-filter-resource-type"
          />
          <datalist id="audit-resource-types">
            <option value="device" />
            <option value="config_template" />
            <option value="hardware_type" />
            <option value="device_type" />
            <option value="firmware" />
            <option value="user" />
            <option value="client" />
          </datalist>
        </div>

        <div className="filter-field">
          <label htmlFor="audit-filter-status">
            {t('audit.filters.status')}
          </label>
          <select
            id="audit-filter-status"
            className="filter-select"
            value={filters.status || ''}
            onChange={(e) => handleFilter('status', e.target.value)}
            data-testid="audit-filter-status"
          >
            <option value="">{t('audit.filters.statusAll')}</option>
            <option value="success">{t('audit.filters.statusSuccess')}</option>
            <option value="failed">{t('audit.filters.statusFailed')}</option>
            <option value="denied">{t('audit.filters.statusDenied')}</option>
          </select>
        </div>

        <div className="filter-field audit-filter-checkbox">
          <label htmlFor="audit-filter-mutations">
            <input
              id="audit-filter-mutations"
              type="checkbox"
              checked={!!filters.exclude_reads}
              onChange={(e) => handleFilter('exclude_reads', e.target.checked)}
              data-testid="audit-filter-mutations"
            />
            {t('audit.filters.mutationsOnly')}
          </label>
        </div>

        <div className="filter-field audit-filter-checkbox">
          <label
            htmlFor="audit-filter-authenticated-only"
            title={t('audit.filters.authenticatedOnlyHint')}
          >
            <input
              id="audit-filter-authenticated-only"
              type="checkbox"
              checked={filters.authenticated_only !== false}
              onChange={(e) =>
                onFilterChange({
                  ...filters,
                  authenticated_only: e.target.checked,
                })
              }
              data-testid="audit-filter-authenticated-only"
            />
            {t('audit.filters.authenticatedOnly')}
          </label>
        </div>

        <button
          type="button"
          className="audit-advanced-toggle"
          onClick={() => setShowAdvanced(v => !v)}
          aria-expanded={showAdvanced}
          aria-controls="audit-advanced-filters"
          data-testid="audit-advanced-toggle"
        >
          {t('audit.filters.advanced')}
          {advancedActiveCount > 0 && (
            <span className="audit-advanced-badge" aria-label={`${advancedActiveCount} active`}>
              {advancedActiveCount}
            </span>
          )}
          <span className={`audit-advanced-chevron${showAdvanced ? ' open' : ''}`} aria-hidden="true">▾</span>
        </button>

        <button
          type="button"
          onClick={() => onFilterChange({})}
          className="filter-reset-btn"
        >
          {t('audit.filters.reset')}
        </button>
      </div>

      {showAdvanced && (
        <div
          id="audit-advanced-filters"
          className="audit-filters audit-filters--advanced"
        >
          <div className="filter-field">
            <label htmlFor="audit-filter-resource-id">
              {t('audit.filters.resourceId')}
            </label>
            <input
              id="audit-filter-resource-id"
              type="text"
              className="filter-input"
              value={filters.resource_id || ''}
              onChange={(e) => handleFilter('resource_id', e.target.value)}
              data-testid="audit-filter-resource-id"
            />
          </div>

          <div className="filter-field">
            <label htmlFor="audit-filter-action">
              {t('audit.filters.action')}
            </label>
            <input
              id="audit-filter-action"
              type="text"
              className="filter-input"
              value={filters.action || ''}
              onChange={(e) => handleFilter('action', e.target.value)}
              data-testid="audit-filter-action"
            />
          </div>

          <div className="filter-field">
            <label htmlFor="audit-filter-actor">{t('audit.filters.actor')}</label>
            <input
              id="audit-filter-actor"
              type="text"
              className="filter-input"
              value={filters.actor_username || ''}
              onChange={(e) => handleFilter('actor_username', e.target.value)}
              data-testid="audit-filter-actor"
            />
          </div>

          <div className="filter-field">
            <label htmlFor="audit-filter-from">{t('audit.filters.fromTimestamp')}</label>
            <input
              id="audit-filter-from"
              type="datetime-local"
              className="filter-input"
              value={fromIsoToInput(filters.from_timestamp)}
              onChange={(e) =>
                handleFilter('from_timestamp', toIsoFromInput(e.target.value) || '')
              }
              data-testid="audit-filter-from"
            />
          </div>

          <div className="filter-field">
            <label htmlFor="audit-filter-to">{t('audit.filters.toTimestamp')}</label>
            <input
              id="audit-filter-to"
              type="datetime-local"
              className="filter-input"
              value={fromIsoToInput(filters.to_timestamp)}
              onChange={(e) =>
                handleFilter('to_timestamp', toIsoFromInput(e.target.value) || '')
              }
              data-testid="audit-filter-to"
            />
          </div>
        </div>
      )}

      {loading ? (
        <div className="audit-loading">{t('audit.loading')}</div>
      ) : items.length === 0 ? (
        <div className="audit-empty" data-testid="audit-empty">
          <p>{t('audit.empty')}</p>
          {Object.keys(filters).length > 0 && (
            <button
              type="button"
              className="filter-reset-btn"
              onClick={() => onFilterChange({})}
              data-testid="audit-empty-clear"
            >
              {t('audit.emptyClearFilters')}
            </button>
          )}
        </div>
      ) : (
        <div className="audit-log-table-wrapper">
          <table className="audit-log-table">
            <thead>
              <tr>
                <th>{t('audit.table.timestamp')}</th>
                <th>{t('audit.table.actor')}</th>
                <th>{t('audit.table.action')}</th>
                <th>{t('audit.table.resource')}</th>
                <th>{t('audit.table.status')}</th>
                <th>{t('audit.table.method')}</th>
                <th>{t('audit.table.path')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((entry) => {
                const isExpanded = expanded === entry.id;
                return (
                  <React.Fragment key={entry.id}>
                    <tr
                      className={`audit-row${isExpanded ? ' expanded' : ''}`}
                      onClick={() => setExpanded(isExpanded ? null : entry.id)}
                      data-testid={`audit-row-${entry.id}`}
                    >
                      <td className="audit-timestamp" title={entry.timestamp}>
                        {formatRelativeTime(entry.timestamp)}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="audit-cell-filter"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (entry.actor_username) {
                              handleFilter('actor_username', entry.actor_username);
                            }
                          }}
                          title={t('audit.filters.actor')}
                        >
                          {entry.actor_username || t('audit.table.anonymous')}
                        </button>
                      </td>
                      <td className="audit-action">
                        <button
                          type="button"
                          className="audit-cell-filter"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFilter('action', entry.action);
                          }}
                          title={t('audit.filters.action')}
                        >
                          {entry.action}
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="audit-cell-filter"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFilter('resource_type', entry.resource_type);
                          }}
                          title={t('audit.filters.resourceType')}
                        >
                          {entry.resource_type}
                        </button>
                        {entry.resource_id ? ` / ${entry.resource_id}` : ''}
                      </td>
                      <td>
                        <span className={`audit-status-badge ${entry.status}`}>
                          <span className="audit-status-icon" aria-hidden="true">
                            {STATUS_ICON[entry.status] || ''}
                          </span>
                          {entry.status}
                        </span>
                      </td>
                      <td>{entry.http_method || '—'}</td>
                      <td className="audit-path">{entry.http_path || '—'}</td>
                    </tr>
                    {isExpanded && (
                      <tr className="audit-expanded-row">
                        <td colSpan={7}>
                          <div className="audit-diff-panel">
                            <UnifiedAuditDiff
                              oldValue={entry.old_value}
                              newValue={entry.new_value}
                            />
                            <div className="audit-diff-meta">
                              {entry.actor_ip && (
                                <div>
                                  <strong>{t('audit.row.ip')}:</strong>
                                  {entry.actor_ip}
                                </div>
                              )}
                              {entry.actor_roles &&
                                entry.actor_roles.length > 0 && (
                                  <div>
                                    <strong>{t('audit.row.roles')}:</strong>
                                    {entry.actor_roles.join(', ')}
                                  </div>
                                )}
                              <div>
                                <strong>
                                  {t('audit.table.timestamp')}:
                                </strong>
                                {formatDate(entry.timestamp)}
                              </div>
                            </div>
                            {entry.error_message && (
                              <div className="audit-diff-error">
                                <strong>{t('audit.row.errorMessage')}:</strong>{' '}
                                {entry.error_message}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="pagination-wrapper">
        <div className="pagination-info">
          <div className="table-info">
            {t('audit.pagination.pageOf', { page, total: totalPages })} · {total} total
          </div>
          <div className="items-per-page">
            <label htmlFor="audit-page-size">
              {t('audit.pagination.pageSize')}
            </label>
            <select
              id="audit-page-size"
              className="items-per-page-select"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              data-testid="audit-page-size"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
            </select>
          </div>
        </div>
        <div className="pagination">
          <button
            type="button"
            className="pagination-btn"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            ← {t('audit.pagination.prev')}
          </button>
          <button
            type="button"
            className="pagination-btn"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            {t('audit.pagination.next')} →
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogTable;
